import { useEffect, useMemo, useState } from 'react';

import { usePermission } from '@hotjar/webapp/domains/authorization';
import { useErrorHandler } from '@hotjar/error-handler';
import type { Site } from '@hotjar/site';
import type {
  SubmitCheckoutParams,
  SubmitCheckoutResult,
} from '@hotjar/webapp/domains/revenue/hooks/useUpsellCheckout';
import type { BillingPeriod } from '@hotjar/subscription-management';
import { ProductType } from '@hotjar/webapp/domains/common/product/types';
import {
  getProductBySku,
  getProductSkusByPlanId,
} from '@hotjar/webapp/domains/common/product/utils';
import { useProductCatalog } from '@hotjar/webapp/domains/common/product/hooks/useProductCatalog';
import { useEffectOnceWithDependencies } from '@hotjar/webapp/hooks/useEffectOnceWithDependencies';
import type { Sku } from '@hotjar/product-catalog';
import { ModalId, useCanSelfServeUpsell, getSalesLink } from '@hotjar/upsell';
import type { Referrer, Context } from '@hotjar/upsell';

import { getPlanSelectModalType, getUpsellSteps } from './utils';
import { Modal } from './components/Modal';
import type {
  SelectedProducts,
  SelectedProductTypes,
  ChangeSelectedProducts,
} from '../MultiProductWrapper/hooks/useMultiProductsState';

export interface Props {
  site: Site;
  selectedProducts: SelectedProducts;
  changeSelectedProducts: ChangeSelectedProducts;
  selectedProductTypes: SelectedProductTypes;
  goPrevStep: () => void;
  onCancel: () => void;
  onSubmit: (params: SubmitCheckoutParams) => Promise<SubmitCheckoutResult>;
  referrer: Referrer;
  billingPeriod: BillingPeriod;
  billingPeriodChanged: boolean;
  setBillingPeriod: (billingPeriod: BillingPeriod) => unknown;
  context: Context;
  selectedSkus: Array<Sku>;
}

export const PlanSelectModal = ({
  site,
  onCancel,
  selectedProducts,
  changeSelectedProducts,
  selectedProductTypes,
  goPrevStep,
  onSubmit,
  referrer,
  billingPeriod,
  billingPeriodChanged,
  setBillingPeriod,
  context,
  selectedSkus,
}: Props): JSX.Element => {
  const handleError = useErrorHandler();

  const modalType = useMemo(
    () => getPlanSelectModalType(selectedProductTypes),
    [selectedProductTypes],
  );

  const [stepList, setStepList] = useState(getUpsellSteps(modalType));
  const [currentStep, setCurrentStep] = useState(stepList.head);

  const isOwner = usePermission('payment-methods-manage');
  const { data: productCatalog, isError: isErrorProductCatalog } = useProductCatalog();

  const goBack = () => {
    if (currentStep?.prev) {
      setCurrentStep(currentStep.prev);
      return;
    }
    // go to previous MultiProducts steps
    goPrevStep();
  };

  const { canSelfServe } = useCanSelfServeUpsell({ site, selectedSkus });

  const onCheckoutClick = async () => {
    const isStandalone = context?.modalId === ModalId.MultiProductPlanSelect;
    if (currentStep?.next && currentStep?.next?.data.step !== 'checkout' && !isStandalone) {
      setCurrentStep(currentStep?.next);
      return;
    }
    if (!canSelfServe) {
      window.open(getSalesLink('MultiProductUpsell'), '_blank', 'noopener noreferrer');
      return;
    }
    const [analyticsProdSku, feedbackProdSku] = getProductSkusByPlanId(site.planId);
    const [currentAnalyticsProduct, currentFeedbackProduct] = [
      getProductBySku(analyticsProdSku, productCatalog),
      getProductBySku(feedbackProdSku, productCatalog),
    ];

    if (!productCatalog || !currentAnalyticsProduct || !currentFeedbackProduct) {
      handleError(
        new Error(
          `Could not get current Analytics and Feedback product by SKU. Plan ID: ${site.planId}`,
        ),
      );
      return;
    }
    const newProducts = {
      [ProductType.Analytics]: selectedProducts.analytics_product ?? currentAnalyticsProduct,
      [ProductType.Feedback]: selectedProducts.feedback_product ?? currentFeedbackProduct,
    };

    try {
      // This `onSubmit` callback is coming from `createGlobalUpsellModal`
      await onSubmit({
        referrer,
        selectedPlans: {
          [site.id]: newProducts,
        },
      });
    } catch (error) {
      if (error && error instanceof Error) {
        handleError(error);
      }
    }
  };

  useEffect(() => {
    setStepList(getUpsellSteps(modalType));
    setCurrentStep(getUpsellSteps(modalType).head);
  }, [modalType]);

  useEffectOnceWithDependencies(
    () => {
      if (isErrorProductCatalog) {
        handleError(new Error(`PlanSelectModal: Error while fetching v2 product catalog.`));
      }
    },
    [isErrorProductCatalog],
    () => isErrorProductCatalog,
  );

  return (
    <Modal
      stepList={stepList}
      currentStep={currentStep}
      selectedProducts={selectedProducts}
      changeSelectedProducts={changeSelectedProducts}
      goBack={goBack}
      onSubmit={onCheckoutClick}
      onCancel={onCancel}
      site={site}
      isOwner={isOwner}
      canSelfServe={canSelfServe}
      billingPeriod={billingPeriod}
      billingPeriodChanged={billingPeriodChanged}
      setBillingPeriod={setBillingPeriod}
      context={context}
    />
  );
};
